/**
 * Tipos e chamadas de "Papéis e permissões".
 *
 * O contrato espelha `api/internal/modulos/conta/perfis.go`. Os SEIS papéis
 * de fábrica (`administrador`, `gerente`, `caixa`, `vendedor`, `estoquista`,
 * `contador`) continuam vindo da matriz ESTÁTICA de
 * `platform/porteiro/papel.go` — somente leitura, sempre. O que mudou é que
 * `GET /perfis` agora também traz os papéis PERSONALIZADOS da loja
 * (`personalizado: true`), que esta tela pode criar, editar e excluir.
 *
 * Quem decide de verdade continua sendo só o porteiro no servidor
 * (CONSTITUICAO.md §4.1) — esta tela só grava a matriz; nunca reimplemente a
 * decisão aqui.
 */
import { api } from './api'

/** Nível de uma permissão — spec 04. */
export type NivelPermissao = 'full' | 'pin' | 'read' | 'none'

export type PapelDaMatriz = {
  /** Identifica o papel nos mapas `niveis`. Para os seis de fábrica é a
   *  própria chave ("administrador" etc.); para personalizado é o uuid. */
  chave: string
  rotulo: string
  descricao: string
  personalizado: boolean
  /** Só é significativo quando `personalizado` é verdadeiro: impede excluir. */
  em_uso: boolean
}

export type PermissaoDaMatriz = {
  chave: string
  rotulo: string
  grupo: string
  /** Nível por CHAVE DE PAPEL (`PapelDaMatriz.chave`). */
  niveis: Record<string, NivelPermissao>
}

export type MatrizDePermissoes = {
  papeis: PapelDaMatriz[]
  permissoes: PermissaoDaMatriz[]
}

export function carregarMatrizDePermissoes(sinal?: AbortSignal): Promise<MatrizDePermissoes> {
  return api.get<MatrizDePermissoes>('/perfis', sinal)
}

/** O corpo de POST /perfis e PUT /perfis/{id}. */
export type PedidoPapelPersonalizado = {
  nome: string
  descricao?: string
  /** Chave de PERMISSÃO (não de papel) → nível. Omitir uma chave, ou mandar
   *  "none", equivale a negar — não precisa mandar as permissões negadas. */
  niveis: Record<string, NivelPermissao>
}

export type PapelPersonalizado = {
  id: string
  nome: string
  descricao: string
  personalizado: true
  em_uso: boolean
  niveis: Record<string, NivelPermissao>
}

export function criarPapelPersonalizado(pedido: PedidoPapelPersonalizado): Promise<PapelPersonalizado> {
  return api.post<PapelPersonalizado>('/perfis', pedido)
}

export function atualizarPapelPersonalizado(
  id: string,
  pedido: PedidoPapelPersonalizado,
): Promise<PapelPersonalizado> {
  return api.put<PapelPersonalizado>(`/perfis/${encodeURIComponent(id)}`, pedido)
}

export function excluirPapelPersonalizado(id: string): Promise<void> {
  return api.delete(`/perfis/${encodeURIComponent(id)}`)
}
